import { useState } from 'react'
import { toast } from 'sonner'
import {
  usePlaces, usePlaceRows, useGeneratePlaces, downloadPdf,
  type WarehousePlace, type PlaceSort,
} from '@/api/warehouse'
import { useAuth } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  normalizeRowCode, rackPreview, zoneForLevel, clampDim, LIMITS, MAX_GENERATED_PLACES,
} from '@/lib/rack-builder'

export default function PlacesPage() {
  const { warehouse } = useAuth()
  const [rowCode, setRowCode] = useState('')
  const [q, setQ] = useState('')
  const [free, setFree] = useState<'' | 'true' | 'false'>('')
  const [zoneF, setZoneF] = useState<'' | 'fuss' | 'stapler'>('')
  const [sort, setSort] = useState<PlaceSort>('barcode')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [rowInput, setRowInput] = useState('')
  const [dims, setDims] = useState({ fields: 6, slots: 3, levels: 5 })
  // RA3-WEB-C: der frühere Fix (Welle W, Download-Minor) landete versehentlich nur in
  // ReceiptDetailPage.tsx — Regal-Etiketten hier hatten weiterhin weder try/catch noch
  // Pending-Guard. Gleiches Muster wie InventurDetailPage.tsx onDownload().
  const [printing, setPrinting] = useState(false)

  const places = usePlaces({
    rowCode: rowCode || undefined, q: q || undefined, free: free || undefined,
    zone: zoneF || undefined, sort, dir,
  })
  const placeRows = usePlaceRows()
  const generate = useGeneratePlaces()

  // Klick auf Spaltenkopf: gleiche Spalte = Richtung umdrehen, neue Spalte = sinnvolle Startrichtung
  // (Belegung startet absteigend — vollste Plaetze zuerst).
  function toggleSort(key: PlaceSort) {
    if (sort === key) { setDir(dir === 'asc' ? 'desc' : 'asc') }
    else { setSort(key); setDir(key === 'belegung' ? 'desc' : 'asc') }
  }
  const arrow = (key: PlaceSort) => (sort === key ? (dir === 'asc' ? ' ▲' : ' ▼') : '')

  const toggle = (b: string) => setSel((s) => {
    const n = new Set(s); if (n.has(b)) n.delete(b); else n.add(b); return n
  })

  const normalizedRow = normalizeRowCode(rowInput)
  const rack = normalizedRow ? rackPreview(normalizedRow, dims.fields, dims.slots, dims.levels) : null

  async function onGenerate() {
    if (!normalizedRow || !rack || rack.overLimit) return
    try {
      const r = await generate.mutateAsync({ row_code: normalizedRow, ...dims })
      toast.success(`${r.created} Plätze angelegt, ${r.skipped_existing} existierten bereits`)
    } catch { /* Fehler-Toast kommt aus useGeneratePlaces().onError */ }
  }
  async function onPrint() {
    if (sel.size === 0) { toast.error('Keine Plätze ausgewählt'); return }
    if (printing) return
    setPrinting(true)
    try {
      await downloadPdf('/warehouse/place/labels-pdf/', 'regal-etiketten.pdf', { barcodes: [...sel] })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Download fehlgeschlagen')
    } finally {
      setPrinting(false)
    }
  }

  const rows: WarehousePlace[] = places.data ?? []
  const freeCount = rows.filter((p) => (p.isAvailable ?? (p.occupancy === 0 && !p.recaptureReservation))).length

  return (
    <div className="p-4 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">Plätze</h1>
        <div className="text-sm text-muted">{rows.length} geladen · {freeCount} frei / {rows.length - freeCount} belegt</div>
      </div>

      {warehouse.lead && (
        <div className="rounded-xl border border-line bg-panel p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-[#108474]" aria-hidden />
            <div className="font-semibold">Regal-Baukasten</div>
            <div className="text-xs text-muted">Regal nachbauen → alle Plätze in einem Rutsch anlegen</div>
          </div>

          <div className="flex flex-wrap gap-4 items-end">
            <label className="text-sm space-y-1">
              <span>Regalreihe</span>
              <Input
                className="w-24 font-mono"
                value={rowInput}
                placeholder="R26"
                onChange={(e) => setRowInput(e.target.value)}
              />
            </label>
            <Stepper label="Fächer" value={dims.fields} max={LIMITS.fields}
              onChange={(v) => setDims({ ...dims, fields: v })} />
            <Stepper label="Plätze je Fach" value={dims.slots} max={LIMITS.slots}
              onChange={(v) => setDims({ ...dims, slots: v })} />
            <Stepper label="Ebenen" value={dims.levels} max={LIMITS.levels}
              onChange={(v) => setDims({ ...dims, levels: v })} />
            <Button
              className="bg-[#108474] hover:bg-[#0d6b5e]"
              onClick={() => void onGenerate()}
              disabled={generate.isPending || !normalizedRow || !rack || rack.overLimit}
            >
              {generate.isPending ? 'Lege an…' : `${rack?.count ?? 0} Plätze anlegen`}
            </Button>
          </div>

          {rowInput && !normalizedRow && (
            <p className="text-sm text-destructive">
              Format: Buchstabe(n) + Zahl, z.B. „R26" — die Nummer wird automatisch zweistellig (r1 → R01).
            </p>
          )}
          {rack?.overLimit && (
            <p className="text-sm text-destructive">
              {rack.count} Plätze überschreiten das Maximum von {MAX_GENERATED_PLACES} pro Lauf — Regal aufteilen.
            </p>
          )}

          {normalizedRow && rack && !rack.overLimit && (
            <div className="space-y-2">
              <RackGrid fields={dims.fields} slots={dims.slots} levels={dims.levels} />
              <div className="text-sm text-muted font-mono">
                {normalizedRow}: {rack.first} … {rack.last}
              </div>
              <div className="flex gap-3 text-xs text-muted">
                <span className="flex items-center gap-1">
                  <span className="inline-block size-2.5 rounded-sm bg-[#FFB300]/40 border border-[#FFB300]" aria-hidden />
                  EB1 zu Fuß
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block size-2.5 rounded-sm bg-[#108474]/20 border border-[#108474]/60" aria-hidden />
                  ab EB2 Stapler
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <select
          className="border border-line rounded-md px-2 py-1.5 text-sm bg-panel font-mono"
          value={rowCode}
          onChange={(e) => setRowCode(e.target.value)}
        >
          <option value="">alle Reihen</option>
          {(placeRows.data ?? []).map((r) => (
            <option key={r.rowCode} value={r.rowCode}>{r.rowCode} ({r.count})</option>
          ))}
        </select>
        <select
          className="border border-line rounded-md px-2 py-1.5 text-sm bg-panel"
          value={zoneF}
          onChange={(e) => setZoneF(e.target.value as typeof zoneF)}
        >
          <option value="">alle Zonen</option>
          <option value="fuss">zu Fuß (EB1)</option>
          <option value="stapler">Stapler (ab EB2)</option>
        </select>
        <select className="border border-line rounded-md px-2 py-1.5 text-sm bg-panel" value={free} onChange={(e) => setFree(e.target.value as typeof free)}>
          <option value="">alle</option><option value="true">nur freie</option><option value="false">nur belegte</option>
        </select>
        <Input className="w-48" placeholder="Suche Barcode…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Button variant="outline" disabled={printing} onClick={() => void onPrint()}>
          {printing ? 'Drucke…' : `Etiketten drucken (${sel.size})`}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-line">
        <table className="w-full text-sm">
          <thead className="bg-panel text-left">
            <tr>
              <th className="p-2 w-8"></th>
              <th className="p-2 cursor-pointer select-none" onClick={() => toggleSort('barcode')}>Barcode{arrow('barcode')}</th>
              <th className="p-2 cursor-pointer select-none" onClick={() => toggleSort('zone')}>Zone{arrow('zone')}</th>
              <th className="p-2 cursor-pointer select-none" onClick={() => toggleSort('ebene')}>Ebene{arrow('ebene')}</th>
              <th className="p-2">Typ</th>
              <th className="p-2 cursor-pointer select-none" onClick={() => toggleSort('belegung')}>Belegung{arrow('belegung')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-line">
                <td className="p-2"><input type="checkbox" checked={sel.has(p.barcode)} onChange={() => toggle(p.barcode)} /></td>
                <td className="p-2 font-mono">{p.barcode}</td>
                <td className="p-2">{p.zone === 'stapler' ? 'Stapler' : 'zu Fuß'}</td>
                <td className="p-2">EB{p.levelNo}</td>
                <td className="p-2">{p.placeType}</td>
                <td className="p-2">{p.recaptureReservation ? <><span className="text-amber-700">Wartet auf erneute Aufnahme</span><div className="text-xs font-mono text-muted">{p.recaptureReservation.paletteNo}</div></> : p.occupancy === 0 ? <span className="text-green-600">frei</span> : `${p.occupancy} Palette(n)`}</td>
              </tr>
            ))}
            {rows.length === 0 && !places.isLoading && (
              <tr><td colSpan={6} className="p-4 text-center text-muted">Keine Plätze — mit dem Generator anlegen.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Stepper(props: { label: string; value: number; max: number; onChange: (v: number) => void }) {
  return (
    <label className="text-sm space-y-1">
      <span>{props.label}</span>
      <div className="flex items-center gap-1">
        <Button type="button" variant="outline" size="sm" className="px-2"
          onClick={() => props.onChange(clampDim(props.value - 1, props.max))}>−</Button>
        <Input
          type="number" className="w-16 text-center" value={props.value}
          onChange={(e) => props.onChange(clampDim(+e.target.value, props.max))}
        />
        <Button type="button" variant="outline" size="sm" className="px-2"
          onClick={() => props.onChange(clampDim(props.value + 1, props.max))}>+</Button>
      </div>
    </label>
  )
}

// Visuelle Regal-Vorschau: Ebenen von oben (EB max) nach unten (EB1), Spalten = Fächer.
// Ab 20 Fächern wird abgeschnitten und "+N" angezeigt, damit die Karte nicht sprengt.
const MAX_RENDER_FIELDS = 20

function RackGrid(props: { fields: number; slots: number; levels: number }) {
  const renderFields = Math.min(props.fields, MAX_RENDER_FIELDS)
  const hidden = props.fields - renderFields
  const levelRows = Array.from({ length: props.levels }, (_, i) => props.levels - i) // EBmax..EB1
  return (
    <div className="overflow-x-auto">
      <div className="inline-block space-y-1">
        {levelRows.map((level) => (
          <div key={level} className="flex items-center gap-1">
            <span className="w-9 shrink-0 text-right text-[10px] text-muted font-mono">EB{level}</span>
            {Array.from({ length: renderFields }, (_, f) => (
              <div
                key={f}
                className={
                  'flex h-6 w-12 items-center justify-center gap-0.5 rounded-sm border ' +
                  (zoneForLevel(level) === 'fuss'
                    ? 'bg-[#FFB300]/40 border-[#FFB300]'
                    : 'bg-[#108474]/20 border-[#108474]/60')
                }
                title={`F${String(f + 1).padStart(2, '0')} · EB${level} · ${props.slots} Plätze`}
              >
                {Array.from({ length: Math.min(props.slots, 6) }, (_, s) => (
                  <span key={s} className="size-1 rounded-full bg-current opacity-60" aria-hidden />
                ))}
              </div>
            ))}
            {hidden > 0 && <span className="text-xs text-muted">+{hidden}</span>}
          </div>
        ))}
        <div className="flex gap-1 pl-10">
          {Array.from({ length: renderFields }, (_, f) => (
            <span key={f} className="w-12 text-center text-[10px] text-muted font-mono">
              F{String(f + 1).padStart(2, '0')}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
